import { useState } from "react";
import { MoreHorizontal, FileText, Edit2 } from "lucide-react";
import { TableActionMenu } from "../ui/TableActionMenu";
import { Card } from "../ui/Card";

// Define a simplified Enrollment type based on what we need
// Ideally this should come from a shared type definition
interface Enrollment {
  _id: string;
  course: {
    title: string;
    _id: string;
  };
  status: string;
  enrollmentDate: string;
  enrollmentExpiresAt?: string;
  deviceLimit?: number;
  certificate?: {
    isIssued: boolean;
  };
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

interface EnrollmentsTableProps {
  enrollments: Enrollment[];
  onIssueCertificate: (enrollmentId: string) => void;
  onEdit: (enrollment: Enrollment) => void;
}

export function EnrollmentsTable({
  enrollments,
  onIssueCertificate,
  onEdit,
}: EnrollmentsTableProps) {
  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);

  const getStatusBadge = (status: string) => {
    let classes = "bg-gray-100 text-gray-800"; // default

    switch (status) {
      case "active":
        classes = "bg-green-100 text-green-800";
        break;
      case "completed":
        classes = "bg-blue-100 text-blue-800";
        break;
      case "cancelled":
        classes = "bg-red-100 text-red-800";
        break;
      case "expired":
        classes = "bg-yellow-100 text-yellow-800";
        break;
      case "suspended":
        classes = "bg-orange-100 text-orange-800";
        break;
    }

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${classes}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <Card className="p-0 border shadow-sm">
        <div className="p-4 sm:p-6 pb-2">
          <h3 className="text-xl font-bold text-gray-900">Enrolment Courses</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[800px] w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Course Name
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Status
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Device at login
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Join Date
                </th>
                <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900">
                  Expiry Date
                </th>
                <th className="px-3 sm:px-6 py-4 w-[50px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {enrollments.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 sm:px-6 py-8 text-center text-gray-500"
                  >
                    No enrollments found.
                  </td>
                </tr>
              ) : (
                enrollments.map((enrollment) => (
                  <tr
                    key={enrollment._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-3 sm:px-6 py-4 font-medium text-[#102040]">
                      {enrollment.course?.title || "Unknown Course"}
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      {getStatusBadge(enrollment.status)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-[#989797]">
                      {enrollment.deviceLimit
                        ? `${enrollment.deviceLimit} Device`
                        : "N/A"}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-[#989797]">
                      {new Date(enrollment.enrollmentDate).toLocaleDateString()}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-[#989797]">
                      {enrollment.enrollmentExpiresAt
                        ? new Date(
                            enrollment.enrollmentExpiresAt,
                          ).toLocaleDateString()
                        : "Lifetime"}
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-right relative">
                      <button
                        className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeMenu?.id === enrollment._id) {
                            setActiveMenu(null);
                          } else {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setActiveMenu({
                              id: enrollment._id,
                              position: {
                                top: rect.bottom,
                                right: window.innerWidth - rect.right,
                              },
                            });
                          }
                        }}
                      >
                        <MoreHorizontal size={20} />
                      </button>

                      <TableActionMenu
                        isOpen={activeMenu?.id === enrollment._id}
                        onClose={() => setActiveMenu(null)}
                        position={activeMenu?.position || null}
                      >
                        <button
                          onClick={() => {
                            onEdit(enrollment);
                            setActiveMenu(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                        >
                          <Edit2 size={14} />
                          Edit Enrollment
                        </button>

                        <button
                          onClick={() => {
                            if (
                              enrollment.status === "completed" &&
                              !enrollment.certificate?.isIssued
                            ) {
                              onIssueCertificate(enrollment._id);
                            }
                            setActiveMenu(null);
                          }}
                          disabled={
                            enrollment.status !== "completed" ||
                            !!enrollment.certificate?.isIssued
                          }
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                            enrollment.status !== "completed" ||
                            enrollment.certificate?.isIssued
                              ? "text-gray-400 cursor-not-allowed"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          <FileText size={14} />
                          {enrollment.certificate?.isIssued
                            ? "Certificate Issued"
                            : "Issue Certificate"}
                        </button>
                      </TableActionMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
